import styled, { css } from 'styled-components';

export const PanelContainer = styled.section(
  ({ theme }) => css`
    max-width: none;
    background: ${theme.co.primary99};
    padding: ${theme.sz.s14} ${theme.sz.s12};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    min-width: 0;
    max-height: 100vh;
    overflow-y: auto;

    @media (max-height: 900px) {
      padding: ${theme.sz.s8} ${theme.sz.s10};
    }

    @media (max-height: 750px) {
      padding: ${theme.sz.s5} ${theme.sz.s8};
    }

    @media (max-width: ${theme.sz.lg}) {
      background: #fff;
      padding: ${theme.sz.s6} ${theme.sz.s5} 0;
      max-height: none;
      overflow-y: visible;
    }
  `,
);

export const Inner = styled.div(
  () => css`
    max-width: 640px;
    margin: 0 auto;
    width: 100%;
  `,
);

export const Eyebrow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    color: ${theme.co.primary10};
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    margin-bottom: ${theme.sz.s6};

    &::before,
    &::after {
      content: '';
      width: 28px;
      height: 2px;
      background: ${theme.co.primary20};
      border-radius: 2px;
    }

    @media (max-height: 900px) {
      margin-bottom: ${theme.sz.s3};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const Title = styled.h2(
  ({ theme }) => css`
    text-align: center;
    font-weight: 800;
    font-size: 3.25rem;
    line-height: 1.18;
    color: ${theme.co.secondary20};
    margin: 0 0 ${theme.sz.s5};
    letter-spacing: -0.01em;

    em {
      font-style: normal;
      color: ${theme.co.primary20};
    }

    @media (max-height: 900px) {
      font-size: 2.5rem;
      margin: 0 0 ${theme.sz.s3};
    }

    @media (max-height: 750px) {
      font-size: 2rem;
      margin: 0 0 ${theme.sz.s2};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const SubTitle = styled.p(
  ({ theme }) => css`
    text-align: center;
    font-size: 1.25rem;
    line-height: 1.5;
    color: ${theme.co.secondary40};
    font-weight: 500;
    margin: 0 0 ${theme.sz.s10};

    strong {
      font-weight: 700;
      color: ${theme.co.secondary20};
    }

    @media (max-height: 900px) {
      margin: 0 0 ${theme.sz.s5};
      font-size: 1.125rem;
    }

    @media (max-height: 750px) {
      margin: 0 0 ${theme.sz.s3};
      font-size: 1rem;
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const CurrencyRow = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.sz.s3};
    margin-bottom: ${theme.sz.s5};
  `,
);

export const CurrencyLabel = styled.label(
  ({ theme }) => css`
    font-size: 0.9375rem;
    color: ${theme.co.secondary40};
    font-weight: 500;

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.875rem;
    }
  `,
);

export const CurrencyDisplay = styled.div(
  ({ theme }) => css`
    background: #fff;
    border: 1px solid ${theme.co.secondary90};
    border-radius: 10px;
    padding: ${theme.sz.s2} ${theme.sz.s4};
    font-weight: 600;
    font-size: 0.9375rem;
    color: ${theme.co.secondary20};
    min-width: 115px;
    text-align: center;

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.875rem;
      padding: ${theme.sz.s2} ${theme.sz.s3};
    }
  `,
);

export const PromoBanner = styled.div(
  ({ theme }) => css`
    background: linear-gradient(135deg, #d4380d 0%, #cf1322 100%);
    border-radius: 10px;
    text-align: center;
    padding: ${theme.sz.s3} ${theme.sz.s5};
    font-weight: 800;
    font-size: 1.0625rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: ${theme.sz.s6};
    box-shadow: 0 2px 8px rgba(207, 19, 34, 0.25);

    @media (max-height: 900px) {
      margin-bottom: ${theme.sz.s4};
      padding: ${theme.sz.s2} ${theme.sz.s4};
    }

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.9375rem;
      padding: ${theme.sz.s2} ${theme.sz.s4};
      margin-bottom: ${theme.sz.s4};
    }
  `,
);

export const FeaturesLine = styled.div(
  ({ theme }) => css`
    text-align: center;
    margin: ${theme.sz.s8} 0 ${theme.sz.s7};
    padding-top: ${theme.sz.s7};
    border-top: 1px solid ${theme.co.primary90};
    font-size: 1.0625rem;
    color: ${theme.co.secondary20};
    font-weight: 700;
    line-height: 1.6;

    span + span::before {
      content: '•';
      color: ${theme.co.secondary60};
      margin: 0 12px;
      font-weight: 400;
    }

    @media (max-height: 900px) {
      margin: ${theme.sz.s4} 0 ${theme.sz.s4};
      padding-top: ${theme.sz.s4};
      font-size: 0.9375rem;
    }

    @media (max-height: 750px) {
      margin: ${theme.sz.s3} 0 ${theme.sz.s3};
      padding-top: ${theme.sz.s3};
    }

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.875rem;
      margin: ${theme.sz.s5} 0 ${theme.sz.s4};
      padding-top: 0;
      border-top: none;
    }
  `,
);

export const Cta = styled.button(
  ({ theme }) => css`
    display: block;
    width: 100%;
    background: ${theme.co.primary20};
    color: ${theme.co.secondary0};
    font-weight: 800;
    font-size: 1.375rem;
    text-align: center;
    padding: ${theme.sz.s6};
    border: none;
    border-radius: 14px;
    box-shadow: 0 4px 0 ${theme.co.primary0};
    cursor: pointer;
    transition:
      background 150ms ease,
      transform 80ms ease,
      box-shadow 80ms ease;
    margin-bottom: ${theme.sz.s5};

    &:hover {
      background: ${theme.co.primary30};
    }

    &:active {
      background: ${theme.co.primary0};
      transform: translateY(2px);
      box-shadow: 0 2px 0 ${theme.co.primary0};
    }

    @media (max-height: 900px) {
      padding: ${theme.sz.s4};
      font-size: 1.25rem;
      margin-bottom: ${theme.sz.s3};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const TrustRow = styled.div(
  ({ theme }) => css`
    text-align: center;
    font-size: 1rem;
    color: ${theme.co.secondary50};
    font-weight: 500;
    margin-bottom: ${theme.sz.s5};

    span + span::before {
      content: '•';
      margin: 0 10px;
      color: ${theme.co.secondary60};
    }

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const DownsellMainLine = styled.span(
  ({ theme }) => css`
    display: block;
    font-weight: 700;
    color: ${theme.co.secondary30};
  `,
);

export const DownsellSecondaryLine = styled.span(
  ({ theme }) => css`
    display: block;
    margin-top: ${theme.sz.s1};
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary30};
  `,
);

export const FreeLink = styled.button(
  ({ theme }) => css`
    display: block;
    width: 100%;
    text-align: center;
    font-size: 1rem;
    color: ${theme.co.secondary50};
    text-decoration: underline;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    @media (max-width: ${theme.sz.lg}) {
      margin-top: ${theme.sz.s4};
      margin-bottom: ${theme.sz.s5};
    }
  `,
);
